import React, { useEffect, useRef, useState } from 'react';
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Switch } from '@/shared/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { GripVertical, Trash2, Plus, X } from 'lucide-react';
import { ChecklistItem, FieldType, ConditionOption } from '../types';
import { FIELD_TYPES, FIELD_TYPE_DESCRIPTIONS } from '../constants';
import { generateId } from '../utils';

interface ChecklistItemProps {
  item: ChecklistItem;
  index: number;
  sectionId: string;
  isNewlyCreated: boolean;
  onUpdate: (updates: Partial<ChecklistItem>) => void;
  onDelete: () => void | Promise<void>;
}

const ChecklistItemComponent: React.FC<ChecklistItemProps> = ({
  item,
  index,
  sectionId,
  isNewlyCreated,
  onUpdate,
  onDelete,
}) => {
  const itemRef = useRef<HTMLDivElement>(null);

  // Drag state
  const [isDragging, setIsDragging] = useState(false);
  const [dragState, setDragState] = useState<{
    isDraggedOver: boolean;
    draggedOverPosition: 'top' | 'bottom' | null;
  }>({
    isDraggedOver: false,
    draggedOverPosition: null,
  });

  // Set up draggable for the item
  useEffect(() => {
    const element = itemRef.current;
    if (!element) return;

    return draggable({
      element,
      getInitialData: () => ({
        type: 'item',
        itemId: item.id,
        sectionId,
        index,
      }),
      onDragStart: () => setIsDragging(true),
      onDrop: () => setIsDragging(false),
    });
  }, [item.id, sectionId, index]);

  // Set up drop target for reordering
  useEffect(() => {
    const element = itemRef.current;
    if (!element) return;

    return dropTargetForElements({
      element,
      getData: ({ input, element }) => {
        const rect = element.getBoundingClientRect();
        const midpoint = rect.top + rect.height / 2;
        const position = input.clientY < midpoint ? 'top' : 'bottom';

        return {
          type: 'item-slot',
          sectionId,
          index: position === 'top' ? index : index + 1,
          position,
        };
      },
      canDrop: ({ source }) => source.data.type === 'item',
      onDragEnter: ({ self }) => {
        const position = self.data.position as 'top' | 'bottom';
        setDragState({
          isDraggedOver: true,
          draggedOverPosition: position,
        });
      },
      onDragLeave: () => {
        setDragState({
          isDraggedOver: false,
          draggedOverPosition: null,
        });
      },
      onDrop: () => {
        setDragState({
          isDraggedOver: false,
          draggedOverPosition: null,
        });
      },
    });
  }, [sectionId, index]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onUpdate({ name: e.target.value });
  };

  const handleFieldTypeChange = (fieldType: FieldType) => {
    const updates: Partial<ChecklistItem> = { field_type: fieldType };

    // Add default condition options when switching to Condition type
    if (fieldType === 'Condition') {
      updates.condition_options = [
        {
          id: generateId(),
          item: item.id,
          label: 'Fair',
          value: 'fair',
          order: 1,
        },
      ];
    } else {
      updates.condition_options = [];
    }

    onUpdate(updates);
  };

  const handleToggleRequired = (checked: boolean) => {
    onUpdate({ is_required: checked });
  };

  const handleAddConditionOption = () => {
    const newOption: ConditionOption = {
      id: generateId(),
      item: item.id,
      label: '',
      value: '',
      order: (item.condition_options.length || 0) + 1,
    };
    const currentOptions = item.condition_options || [];
    onUpdate({ condition_options: [...currentOptions, newOption] });
  };

  const handleUpdateConditionOption = (optionId: string, updates: Partial<ConditionOption>) => {
    const updatedOptions = (item.condition_options || []).map((option) =>
      option.id === optionId ? { ...option, ...updates } : option
    );
    onUpdate({ condition_options: updatedOptions });
  };

  const handleDeleteConditionOption = (optionId: string) => {
    const updatedOptions = (item.condition_options || []).filter(
      (option) => option.id !== optionId
    );
    onUpdate({ condition_options: updatedOptions });
  };

  return (
    <div className="relative">
      {/* Drop indicator - top */}
      {dragState.isDraggedOver && dragState.draggedOverPosition === 'top' && (
        <div className="absolute -top-2 left-0 right-0 h-1 bg-blue-500 rounded-full z-10 animate-pulse" />
      )}

      <div
        ref={itemRef}
        data-item-id={item.id}
        className={`bg-white border rounded-lg p-3 sm:p-4 transition-all shadow-sm hover:shadow-md ${
          isDragging ? 'opacity-50 scale-95 rotate-1' : ''
        } ${dragState.isDraggedOver ? 'ring-2 ring-blue-200' : ''}`}
      >
        {/* Item Header */}
        <div className="space-y-3 sm:space-y-0 sm:flex sm:items-center sm:gap-3 mb-4">
          <div className="text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing transition-colors duration-200 hidden sm:block">
            <GripVertical className="h-4 w-4" />
          </div>

          <div className="flex-1 min-w-0">
            <Input
              value={item.name}
              onChange={handleNameChange}
              placeholder="Item name..."
              className={`font-medium text-base w-full ${
                isNewlyCreated && !item.name.trim()
                  ? 'bg-orange-50 border-orange-200 placeholder:text-orange-400'
                  : ''
              }`}
            />
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <div className="flex-1 sm:w-48">
              <Select value={item.field_type} onValueChange={handleFieldTypeChange}>
                <SelectTrigger className="text-sm">
                  <SelectValue placeholder="Select field type" />
                </SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map((fieldType) => (
                    <SelectItem key={fieldType} value={fieldType}>
                      <div>
                        <div className="font-medium text-sm">{fieldType}</div>
                        <div className="text-xs text-gray-500 hidden sm:block">
                          {FIELD_TYPE_DESCRIPTIONS[fieldType as keyof typeof FIELD_TYPE_DESCRIPTIONS]}
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={onDelete}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 shrink-0"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Field Configuration */}
        <div className="space-y-4">
          {/* Condition Options */}
          {item.field_type === 'Condition' && (
            <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-sm">Condition Options</h4>
                <Button variant="outline" size="sm" onClick={handleAddConditionOption}>
                  <Plus className="h-3 w-3 sm:mr-1" />
                  <span className="hidden sm:inline">Add Option</span>
                </Button>
              </div>

              <div className="space-y-2 sm:space-y-3">
                {(item.condition_options || []).map((option: ConditionOption) => (
                  <div key={option.id} className="flex items-center gap-2">
                    <Input
                      value={option.label}
                      onChange={(e) =>
                        handleUpdateConditionOption(option.id, {
                          label: e.target.value,
                          value: e.target.value.toLowerCase().replace(/\s+/g, '_'),
                        })
                      }
                      placeholder="Option label (e.g., Good, Fair, Poor)"
                      className="flex-1 text-sm"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteConditionOption(option.id)}
                      className="text-red-600 hover:text-red-700 shrink-0"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Field Requirements */}
          <div className="flex flex-wrap items-center gap-4 pt-3 sm:pt-2 border-t">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Required Field</label>
              <Switch checked={item.is_required} onCheckedChange={handleToggleRequired} />
            </div>
          </div>
        </div>
      </div>

      {/* Drop indicator - bottom */}
      {dragState.isDraggedOver && dragState.draggedOverPosition === 'bottom' && (
        <div className="absolute -bottom-2 left-0 right-0 h-1 bg-blue-500 rounded-full z-10 animate-pulse" />
      )}
    </div>
  );
};

export default ChecklistItemComponent;
